import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { loadDynamicLanguage } from '../i18n';

export default function LanguageSwitcher({ className = '', variant = 'default' }) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith('en') ? 'en' : (i18n.language || 'tr');
  const [languages, setLanguages] = useState([
    { code: 'tr', name: 'Turkce' },
    { code: 'en', name: 'English' },
  ]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/translations/public/languages`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.languages?.length) {
          setLanguages(data.languages);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectLanguage = async (lang) => {
    if (lang === currentLang) { setOpen(false); return; }
    setLoading(true);
    const ok = await loadDynamicLanguage(lang);
    if (ok) {
      i18n.changeLanguage(lang);
    }
    setLoading(false);
    setOpen(false);
  };

  const getLangName = (code) => {
    const found = languages.find(l => l.code === code);
    return found?.name || code;
  };

  if (languages.length <= 2) {
    const otherLang = currentLang === 'tr' ? 'en' : 'tr';
    return (
      <button
        onClick={() => selectLanguage(otherLang)}
        className={`flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-navy ${className}`}
        data-testid="language-switcher"
        title={getLangName(otherLang)}
      >
        <Globe className="w-4 h-4" />
        <span>{otherLang.toUpperCase()}</span>
      </button>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-navy ${
          variant === 'minimal' ? '' : 'px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 hover:border-slate-300'
        } ${className}`}
        data-testid="language-switcher"
      >
        <Globe className="w-4 h-4" />
        <span>{currentLang.toUpperCase()}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 bottom-full mb-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 min-w-[160px] py-1 max-h-[200px] overflow-y-auto">
          {languages.map(lang => (
            <button
              key={lang.code}
              onClick={() => selectLanguage(lang.code)}
              disabled={loading}
              className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
                lang.code === currentLang ? 'text-navy font-medium bg-slate-50' : 'text-slate-700'
              }`}
              data-testid={`lang-option-${lang.code}`}
            >
              <span className="flex items-center gap-2">
                <span className="font-mono text-xs uppercase">{lang.code}</span>
                <span>{lang.name}</span>
              </span>
              {lang.code === currentLang && <Check className="w-3.5 h-3.5 text-navy" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
